import i18n from '../i18n/config';

/**
 * Error handler utility for API services
 * Provides translated error messages based on error type
 */
class ErrorHandler {
    /**
     * Handle API errors with translations
     * @param {Error} error - The error object
     * @returns {Error} - Error with translated message
     */
    static handleError(error) {
        // Helper — copy metadata (code, data, status) from src onto a newly-wrapped
        // Error so callers can branch on specific error codes (e.g. INSUFFICIENT_CREDITS).
        const withMeta = (message, src) => {
            const out = new Error(message);
            if (src?.code)   out.code = src.code;
            if (src?.data)   out.data = src.data;
            if (src?.status) out.status = src.status;
            return out;
        };

        if (error.response) {
            // Server responded with error
            const serverMessage = error.response.data?.message;
            const meta = {
                code:   error.response.data?.code,
                data:   error.response.data?.data,
                status: error.response.status,
            };
            if (serverMessage) return withMeta(serverMessage, meta);
            return withMeta(i18n.t('errors.serverError'), meta);
        } else if (error.request) {
            // Request made but no response
            return new Error(i18n.t('errors.noResponseFromServer'));
        } else {
            // Something else happened — typically an Error already thrown by the
            // api.js response interceptor, which already carries code/data/status.
            return withMeta(error.message || i18n.t('errors.unexpectedError'), error);
        }
    }

    /**
     * Get translated error message for network issues
     * @returns {string} - Translated network error message
     */
    static getNetworkError() {
        return i18n.t('errors.networkError');
    }

    /**
     * Get translated session expired message
     * @returns {string} - Translated session expired message
     */
    static getSessionExpiredError() {
        return i18n.t('errors.sessionExpired');
    }

    /**
     * Get translated server error message
     * @returns {string} - Translated server error message
     */
    static getServerError() {
        return i18n.t('errors.serverError');
    }

    /**
     * Get translated validation error message
     * @returns {string} - Translated validation error message
     */
    static getValidationError() {
        return i18n.t('errors.validationError');
    }
}

export default ErrorHandler;